import { restartableTask } from 'ember-concurrency';
import { defaultErrorHandler, type TError } from 'volta/utils/error-utils';

import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type StoreService from './store';
import type PlanningSettingRule from 'volta/models/planning-setting-rule';
import type LocalPreferencesService from './local-preferences';
import type Owner from '@ember/owner';

export default class RulesEngineService extends Service {
  // Services
  // ~~~~~

  @service store!: StoreService;
  @service localPreferences!: LocalPreferencesService;

  @tracked rules: PlanningSettingRule[] = [];

  constructor(owner: Owner) {
    super(owner);
    this.fetchRulesTask.perform();
  }

  fetchRulesTask = restartableTask(async () => {
    try {
      const rulesJson =
        (await this.localPreferences.getItem<object[]>('planningSettingRules')) ?? [];
      this.rules = rulesJson.map((rule: object) => {
        return this.store.createRecord<PlanningSettingRule>('planning-setting-rule', rule);
      });
    } catch (error) {
      return defaultErrorHandler(error as TError);
    }
  });

  addRule(rule: PlanningSettingRule) {
    this.rules = [...new Set([...this.rules, rule])];
    this.saveRules();
  }

  removeRule(rule: PlanningSettingRule) {
    this.rules = this.rules.filter((r) => r.id !== rule.id);
    this.saveRules();
  }

  updateRule(rule: PlanningSettingRule) {
    this.rules = this.rules.map((r) => {
      return r.id === rule.id ? rule : r;
    });
    this.saveRules();
  }

  sortRules(rules: PlanningSettingRule[]) {
    this.rules = rules.slice();
    this.saveRules();
  }

  private saveRules() {
    this.localPreferences.setItem(
      'planningSettingRules',
      this.rules.map((rule) => rule.toJSON({ includeId: true }))
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'rules-engine-service': LocalPreferencesService;
  }
}
